import { z } from "zod/v4";
import {
  type IdrCreateGraphSchedule,
  IdrCreateGraphScheduleSchema,
} from "@hightouch/lib/idr/create-schedule";
import {
  SURVIVORSHIP_TYPES,
  SurvivorshipTypes,
} from "@hightouch/lib/idr/golden-record/constants";
import { THRESHOLDS } from "@hightouch/lib/idr/v2/probabilistic/thresholds";
import {
  IdrGraphDocumentGoldenRecordSchema,
  IdrGraphDocumentLimitRuleSchema,
  IdrGraphDocumentMergeRuleSchema,
  IdrGraphDocumentModelSchema,
  IdrGraphDocumentSourcePrioritySchema,
} from "./graph-document-schema";

const UpdateGraphOperationSchema = z
  .object({
    action: z.literal("update_graph"),
    name: z.string().min(1).optional(),
    previousName: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    previousDescription: z.string().nullable().optional(),
  })
  .strict()
  .refine(
    ({ name, description }) => name !== undefined || description !== undefined,
    "An update_graph operation must change at least one field",
  )
  .refine(
    ({ name, previousName }) =>
      (name === undefined) === (previousName === undefined),
    "A name update must include the previous name",
  )
  .refine(
    ({ description, previousDescription }) =>
      (description === undefined) === (previousDescription === undefined),
    "A description update must include the previous description",
  );

const SetMergeRulesOperationSchema = z
  .object({
    action: z.literal("set_merge_rules"),
    mergeRules: z.array(IdrGraphDocumentMergeRuleSchema),
    previousMergeRules: z.array(IdrGraphDocumentMergeRuleSchema),
  })
  .strict();

const SetLimitRulesOperationSchema = z
  .object({
    action: z.literal("set_limit_rules"),
    limitRules: z.array(IdrGraphDocumentLimitRuleSchema),
    previousLimitRules: z.array(IdrGraphDocumentLimitRuleSchema),
    fullRerunRequired: z.boolean(),
  })
  .strict();

export const IdrOutputTableNameSchema = z
  .string()
  .regex(/^\w+$/, "Only alphanumeric characters and underscores are allowed");

const SetOutputTableOperationSchema = z
  .object({
    action: z.literal("set_output_table"),
    outputTable: IdrOutputTableNameSchema,
    previousOutputTable: z.string().min(1),
  })
  .strict();

const EditScheduleSchema = z.custom<IdrCreateGraphSchedule>(
  (value) => IdrCreateGraphScheduleSchema.safeParse(value).success,
  "Invalid IDR schedule",
);

const SetScheduleOperationSchema = z
  .object({
    action: z.literal("set_schedule"),
    schedule: EditScheduleSchema,
    previousSchedule: EditScheduleSchema,
    schedulePaused: z.boolean(),
    previousSchedulePaused: z.boolean(),
  })
  .strict();

const EditableProbabilisticConfigSchema = z
  .object({
    enabled: z.boolean(),
    thresholds: z.array(
      z
        .object({
          threshold: z.enum(THRESHOLDS),
        })
        .strict(),
    ),
  })
  .strict()
  .superRefine(({ enabled, thresholds }, ctx) => {
    if (enabled && thresholds.length === 0) {
      ctx.addIssue({
        code: "custom",
        message:
          "Enabled probabilistic configuration requires a confidence level",
        path: ["thresholds"],
      });
    }
  });

const SetProbabilisticConfigOperationSchema = z
  .object({
    action: z.literal("set_probabilistic_config"),
    previous: EditableProbabilisticConfigSchema,
    desired: EditableProbabilisticConfigSchema,
  })
  .strict();

const GoldenRecordSurvivorshipSchema = z.union([
  z
    .object({
      survivorship: z.literal(SurvivorshipTypes.SOURCE_PRIORITY),
      sourcePriority: z.array(IdrGraphDocumentSourcePrioritySchema).min(1),
    })
    .strict(),
  z
    .object({
      survivorship: z
        .enum(SURVIVORSHIP_TYPES)
        .exclude([SurvivorshipTypes.SOURCE_PRIORITY]),
      sourcePriority: z.never().optional(),
    })
    .strict(),
]);

const UpdateGoldenRecordSurvivorshipOperationSchema = z
  .object({
    action: z.literal("update_golden_record_survivorship"),
    columns: z
      .array(
        z
          .object({
            columnName: z.string().min(1),
            previous: GoldenRecordSurvivorshipSchema,
            desired: GoldenRecordSurvivorshipSchema,
          })
          .strict(),
      )
      .min(1),
  })
  .strict();

const UpdateModelsOperationSchema = z
  .object({
    action: z.literal("update_models"),
    previous: z.array(IdrGraphDocumentModelSchema),
    desired: z.array(IdrGraphDocumentModelSchema),
    fullRerunRequired: z.boolean(),
  })
  .strict();

const UpdateGoldenRecordStructureOperationSchema = z
  .object({
    action: z.literal("update_golden_record_structure"),
    previous: IdrGraphDocumentGoldenRecordSchema.nullable(),
    desired: IdrGraphDocumentGoldenRecordSchema.nullable(),
  })
  .strict();

export const IdrGraphDocumentEditOperationSchema = z.discriminatedUnion(
  "action",
  [
    UpdateGraphOperationSchema,
    SetMergeRulesOperationSchema,
    SetLimitRulesOperationSchema,
    SetOutputTableOperationSchema,
    SetScheduleOperationSchema,
    SetProbabilisticConfigOperationSchema,
    UpdateGoldenRecordSurvivorshipOperationSchema,
    UpdateModelsOperationSchema,
    UpdateGoldenRecordStructureOperationSchema,
  ],
);

export type IdrGraphDocumentEditOperation = z.infer<
  typeof IdrGraphDocumentEditOperationSchema
>;
